import { create } from "zustand";
import {
  getExpiresAt,
  storeExpiresAt,
  clearExpiresAt,
} from "@/features/auth/lib/token";
import type { TenantPermissions } from "@/features/auth/lib/api";
import { config } from "@/config";
import { setSentryUser, setSentryWorkspace } from "@/lib/sentry";
import { identifyUser, setTenantGroup, resetAnalytics } from "@/lib/analytics";
import { deriveUserRole } from "@/lib/analytics-utils";

interface AuthUser {
  sub: string;
  email: string;
  acs: string[];
  name?: string | null;
  picture?: string | null;
}

interface LoginData {
  user: AuthUser;
  tenants: TenantPermissions[];
  expiresAt: number;
  csrfToken: string;
}

interface AuthState {
  user: AuthUser | null;
  tenants: TenantPermissions[];
  activeTenantId: string | null;
  lastVisitedRoute: string | null;
  /** Maps pathname → raw search string (without leading `?`) for routes that need state restoration. */
  lastRouteSearch: Record<string, string>;
  isAuthenticated: boolean;
  csrfToken: string | null;
  login: (data: LoginData) => void;
  logout: () => Promise<void>;
  setActiveTenant: (tenantId: string) => void;
  setLastVisitedRoute: (route: string) => void;
  setLastRouteSearch: (pathname: string, search: string) => void;
  updateTenants: (tenants: TenantPermissions[]) => void;
  updateSession: (expiresAt: number, csrfToken: string, tenants?: TenantPermissions[]) => void;
  updateUserAcs: (acs: string[]) => void;
  updateUserPicture: (picture: string | null) => void;
}

const TENANTS_STORAGE_KEY = "nexus_tenants";
const ACTIVE_TENANT_KEY = "nexus_active_tenant";
const LAST_ROUTE_KEY = "nexus_last_route";
const LAST_ROUTE_SEARCH_KEY = "nexus_last_route_search";
const USER_STORAGE_KEY = "nexus_user";
const CSRF_SESSION_KEY = "nexus_csrf";
/**
 * Search params remembered for this browser session only: an ad-hoc My Brand must not
 * outlive the session, or it would replace the configured default on reopen.
 */
const SESSION_ONLY_SEARCH_PARAMS = ["myBrand"] as const;

function stripSessionOnlyParams(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of SESSION_ONLY_SEARCH_PARAMS) params.delete(key);
  return params.toString();
}

function stripSessionOnlyParamsFromMap(searches: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(searches).map(([pathname, search]) => [pathname, stripSessionOnlyParams(search)]),
  );
}

function parseRouteSearchMap(raw: string | null): Record<string, string> | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      Object.values(parsed as object).every((v) => typeof v === "string")
    ) {
      return parsed as Record<string, string>;
    }
  } catch { /* ignore corrupt data */ }
  return null;
}

/** Same-tab session copy first (keeps My Brand across a reload), else the long-lived copy. */
function hydrateLastRouteSearch(): Record<string, string> {
  const sessionCopy = parseRouteSearchMap(readSessionStorage(LAST_ROUTE_SEARCH_KEY));
  if (sessionCopy) return sessionCopy;
  const persisted = parseRouteSearchMap(readStorage(LAST_ROUTE_SEARCH_KEY));
  return persisted ? stripSessionOnlyParamsFromMap(persisted) : {};
}

function hydrateFromStorage(): {
  user: AuthUser | null;
  tenants: TenantPermissions[];
  activeTenantId: string | null;
  lastVisitedRoute: string | null;
  lastRouteSearch: Record<string, string>;
  isAuthenticated: boolean;
} {
  const expiresAt = getExpiresAt();
  const userJson = readStorage(USER_STORAGE_KEY);
  const lastVisitedRoute = readStorage(LAST_ROUTE_KEY);
  const lastRouteSearch = hydrateLastRouteSearch();

  if (!expiresAt || !userJson) {
    return { user: null, tenants: [], activeTenantId: null, lastVisitedRoute, lastRouteSearch, isAuthenticated: false };
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  if (expiresAt <= nowSeconds) {
    return { user: null, tenants: [], activeTenantId: null, lastVisitedRoute, lastRouteSearch, isAuthenticated: false };
  }

  let user: AuthUser | null = null;
  try {
    user = JSON.parse(userJson) as AuthUser;
  } catch { /* ignore corrupt data */ }

  if (!user) {
    return { user: null, tenants: [], activeTenantId: null, lastVisitedRoute, lastRouteSearch, isAuthenticated: false };
  }

  let tenants: TenantPermissions[] = [];
  try {
    const raw = localStorage.getItem(TENANTS_STORAGE_KEY);
    if (raw) tenants = JSON.parse(raw) as TenantPermissions[];
  } catch { /* ignore corrupt data */ }

  const activeTenantId = readStorage(ACTIVE_TENANT_KEY) ?? tenants[0]?.id ?? null;

  return {
    user,
    tenants,
    activeTenantId,
    lastVisitedRoute,
    lastRouteSearch,
    isAuthenticated: true,
  };
}

function readStorage(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

function readSessionStorage(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}

function writeLastRouteSearch(searches: Record<string, string>): void {
  try {
    sessionStorage.setItem(LAST_ROUTE_SEARCH_KEY, JSON.stringify(searches));
  } catch { /* storage unavailable — in-memory state still holds the session copy */ }
  localStorage.setItem(LAST_ROUTE_SEARCH_KEY, JSON.stringify(stripSessionOnlyParamsFromMap(searches)));
}

const _hydrated = hydrateFromStorage();

// Hydrate Sentry identity on page load when session is already active.
if (_hydrated.isAuthenticated && _hydrated.user) {
  setSentryUser({ id: _hydrated.user.sub, email: _hydrated.user.email });
  setSentryWorkspace(_hydrated.activeTenantId);

  // Hydrate Amplitude identity (mirrors Sentry pattern).
  const _hydratedTenant = _hydrated.tenants.find((t) => t.id === _hydrated.activeTenantId);
  identifyUser(_hydrated.user.sub, {
    user_role: deriveUserRole(_hydratedTenant?.permissions ?? []),
    user_email: _hydrated.user.email,
  });
  if (_hydratedTenant) {
    setTenantGroup(_hydratedTenant.id, _hydratedTenant.name, _hydratedTenant.type);
  }
}

export const useAuth = create<AuthState>((set) => ({
  ..._hydrated,
  csrfToken: _hydrated.isAuthenticated ? readStorage(CSRF_SESSION_KEY) : null,

  login: (data) => {
    const { user, tenants, expiresAt, csrfToken } = data;

    storeExpiresAt(expiresAt);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    localStorage.setItem(TENANTS_STORAGE_KEY, JSON.stringify(tenants));
    localStorage.setItem(CSRF_SESSION_KEY, csrfToken);
    const activeTenantId = tenants[0]?.id ?? null;
    if (activeTenantId) localStorage.setItem(ACTIVE_TENANT_KEY, activeTenantId);

    setSentryUser({ id: user.sub, email: user.email });
    setSentryWorkspace(activeTenantId);

    // Identify in Amplitude
    const loginTenant = tenants.find((t) => t.id === activeTenantId);
    identifyUser(user.sub, {
      user_role: deriveUserRole(loginTenant?.permissions ?? []),
      user_email: user.email,
    });
    if (loginTenant) {
      setTenantGroup(loginTenant.id, loginTenant.name, loginTenant.type);
    }

    set({
      user,
      tenants,
      activeTenantId,
      csrfToken,
      isAuthenticated: true,
    });
  },

  logout: async () => {
    // Immediately gate new queries — components see unauthenticated state before any async work
    set({ isAuthenticated: false });
    // Dynamic import to avoid circular dep: useAuth → query-client → api-client → useAuth
    const { queryClient } = await import("@/lib/query-client");
    // Abort in-flight requests via AbortSignal before the session cookie is invalidated
    await queryClient.cancelQueries();
    try {
      await fetch(`${config.authUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Server may be unreachable — still clear local state
    }

    clearExpiresAt();
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(TENANTS_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_TENANT_KEY);
    localStorage.removeItem(CSRF_SESSION_KEY);
    // lastVisitedRoute + lastRouteSearch intentionally NOT cleared on logout — restore position after re-login.
    // Session-only params (My Brand) end with the session, so drop them here.
    try { sessionStorage.removeItem(LAST_ROUTE_SEARCH_KEY); } catch { /* storage unavailable */ }
    setSentryUser(null);
    setSentryWorkspace(null);
    resetAnalytics();
    queryClient.clear();
    set((state) => ({
      user: null,
      tenants: [],
      activeTenantId: null,
      csrfToken: null,
      lastRouteSearch: stripSessionOnlyParamsFromMap(state.lastRouteSearch),
    }));
  },

  setActiveTenant: (tenantId) => {
    localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
    setSentryWorkspace(tenantId);

    // Re-identify tenant group in Amplitude
    const state = useAuth.getState();
    const switchedTenant = state.tenants.find((t) => t.id === tenantId);
    if (switchedTenant) {
      setTenantGroup(switchedTenant.id, switchedTenant.name, switchedTenant.type);
      identifyUser(state.user?.sub ?? '', {
        user_role: deriveUserRole(switchedTenant.permissions),
        user_email: state.user?.email,
      });
    }

    set({ activeTenantId: tenantId });
  },

  setLastVisitedRoute: (route) => {
    localStorage.setItem(LAST_ROUTE_KEY, route);
    set({ lastVisitedRoute: route });
  },

  setLastRouteSearch: (pathname, search) => {
    set((state) => {
      // A route that unmounts because of logout still records its URL here.
      const recorded = state.isAuthenticated ? search : stripSessionOnlyParams(search);
      const updated = { ...state.lastRouteSearch, [pathname]: recorded };
      writeLastRouteSearch(updated);
      return { lastRouteSearch: updated };
    });
  },

  updateTenants: (tenants) => {
    localStorage.setItem(TENANTS_STORAGE_KEY, JSON.stringify(tenants));
    set((state) => {
      const stillExists = tenants.some((t) => t.id === state.activeTenantId);
      const activeTenantId = stillExists ? state.activeTenantId : tenants[0]?.id ?? null;
      if (activeTenantId !== state.activeTenantId) setSentryWorkspace(activeTenantId);
      return { tenants, activeTenantId };
    });
  },

  updateUserAcs: (acs) => {
    set((state) => {
      if (!state.user) return state;
      const nextUser = { ...state.user, acs };
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
      return { user: nextUser };
    });
  },

  updateUserPicture: (picture) => {
    set((state) => {
      if (!state.user) return state;
      const nextUser = { ...state.user, picture };
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
      return { user: nextUser };
    });
  },

  updateSession: (expiresAt, csrfToken, tenants) => {
    storeExpiresAt(expiresAt);
    localStorage.setItem(CSRF_SESSION_KEY, csrfToken);
    set((state) => {
      // When the session was expired on page load, user is null in state but
      // still in localStorage — restore it so name/picture are available
      // without requiring a manual page refresh.
      let user = state.user;
      if (!user) {
        try {
          const raw = localStorage.getItem(USER_STORAGE_KEY);
          if (raw) user = JSON.parse(raw) as AuthUser;
        } catch { /* ignore corrupt data */ }
      }

      const activeTenantId = tenants
        ? (tenants.some((t) => t.id === state.activeTenantId) ? state.activeTenantId : tenants[0]?.id ?? null)
        : state.activeTenantId;
      if (activeTenantId !== state.activeTenantId) setSentryWorkspace(activeTenantId);
      return {
        csrfToken,
        user,
        ...(tenants ? { tenants } : {}),
        isAuthenticated: true,
        activeTenantId,
      };
    });
    if (tenants) {
      localStorage.setItem(TENANTS_STORAGE_KEY, JSON.stringify(tenants));
    }
  },
}));
